import { workflowTaskTitle, workflowTaskDescription } from "@/lib/workflow-display";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { ApprovalsPanel } from "@/components/timekeeping/ApprovalsPanel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { useDateLocale } from "@/hooks/useDateLocale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2, ClipboardList, ListTodo, Clock, ThumbsUp, ThumbsDown, Send,
  User, Users, Building2, Calendar, Monitor, Eye, EyeOff, ChevronDown, ChevronUp, Briefcase,
  Archive, Trash2, XCircle, Check, X, CalendarCheck, CalendarX, MapPin, Video, ChevronRight, ChevronLeft, ShieldAlert,
  CalendarDays, Link2, RefreshCw, Loader2, FileDown, ExternalLink, Mail, FileText, RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import { getContractTypeLabel } from "@/lib/employment-contract-types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListSkeleton } from "@/components/PageSkeleton";
import {
  type WorkflowTask,
  type SubmittedTask,
  type TaskEmployeeCard,
  type FreeSlot,
  apiGetMyWorkflowTasks, apiCompleteWorkflowTask, apiGetMySubmittedTasks,
  apiGetTaskEmployeeCard, apiArchiveWorkflowTask, apiDeleteWorkflowTask,
  apiArchiveSubmittedTask, apiDeleteSubmittedTask,
  apiUnarchiveWorkflowTask, apiGetArchivedWorkflowTasks,
  apiUnarchiveSubmittedTask, apiGetArchivedSubmittedTasks,
  apiGenerateDocumentsFromTemplates,
  apiScheduleInterview, apiSubmitInterviewFeedback, apiHrInterviewDecision,
  apiDeleteRecruitmentApplication, getTenantSlug,
  apiGetCalendarStatus, apiGetFreeSlots, apiSetInterviewAvailability, apiCreateCalendarMeeting, getCalendarOAuthUrl,
  apiGetCompanyDetails, apiDownloadRecruitmentResume,
  apiGetTeamLeaveOverlap, type TeamLeaveOverlap,
} from "@/lib/api";

export default function MyTasks() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();

  const canReviewTimesheets = user?.role.mainRole === "admin" || user?.role.mainRole === "hr"
    || !!user?.isLineManager || user?.role.subRole === "line_manager";
  const tenantSlug = getTenantSlug();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdParam = searchParams.get("taskId");
  const tabParam = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState<"assigned" | "submitted">(
    tabParam === "submitted" ? "submitted" : "assigned",
  );

  useEffect(() => {
    if (tabParam === "submitted" && activeTab !== "submitted") setActiveTab("submitted");
    if (tabParam !== "submitted" && activeTab !== "assigned") setActiveTab("assigned");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const { data: tasks = [], isLoading } = useQuery<WorkflowTask[]>({
    queryKey: ["workflow-tasks", tenantSlug],
    queryFn: apiGetMyWorkflowTasks,
  });

  const { data: submittedTasks = [], isLoading: isLoadingSubmitted } = useQuery<SubmittedTask[]>({
    queryKey: ["workflow-tasks-submitted", tenantSlug],
    queryFn: apiGetMySubmittedTasks,
  });

  const [showArchivedSubmitted, setShowArchivedSubmitted] = useState(false);

  const { data: archivedSubmittedTasks = [], isLoading: isLoadingArchivedSubmitted } = useQuery<SubmittedTask[]>({
    queryKey: ["workflow-tasks-archived-submitted", tenantSlug],
    queryFn: apiGetArchivedSubmittedTasks,
    enabled: showArchivedSubmitted,
    staleTime: 30_000,
  });

  const archiveSubmittedMutation = useMutation({
    mutationFn: (id: number) => apiArchiveSubmittedTask(id),
    onSuccess: () => {
      toast({ title: "Submission archived" });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-submitted"] });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-archived-submitted"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const unarchiveSubmittedMutation = useMutation({
    mutationFn: (id: number) => apiUnarchiveSubmittedTask(id),
    onSuccess: () => {
      toast({ title: "Submission restored" });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-submitted"] });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-archived-submitted"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteSubmittedMutation = useMutation({
    mutationFn: (id: number) => apiDeleteSubmittedTask(id),
    onSuccess: () => {
      toast({ title: "Submission deleted" });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-submitted"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const [actionTask, setActionTask] = useState<WorkflowTask | null>(null);
  const [actionType, setActionType] = useState<"approved" | "rejected" | "done" | "accepted" | "declined">("approved");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [scheduleTask, setScheduleTask] = useState<WorkflowTask | null>(null);
  const [feedbackTask, setFeedbackTask] = useState<WorkflowTask | null>(null);
  const [reviewTask, setReviewTask] = useState<WorkflowTask | null>(null);
  const [gdprTask, setGdprTask] = useState<WorkflowTask | null>(null);

  const pendingTasks = tasks.filter((tk) => tk.status === "pending");
  const completedTasks = tasks.filter((tk) => tk.status !== "pending");

  const openAction = (task: WorkflowTask, type: "approved" | "rejected" | "done" | "accepted" | "declined") => {
    setActionTask(task);
    setActionType(type);
    setNotes("");
  };

  // Auto-open the task referenced in the URL (from notification deep link)
  useEffect(() => {
    if (!taskIdParam || tasks.length === 0 || actionTask) return;
    const target = tasks.find((t) => String(t.id) === taskIdParam && t.status === "pending");
    if (target) {
      if (target.task_type === "interview_schedule") setScheduleTask(target);
      else if (target.task_type === "interview_feedback") setFeedbackTask(target);
      else if (target.task_type === "interview_review") setReviewTask(target);
      else if (target.task_type === "gdpr_deletion") setGdprTask(target);
      else {
        // Open dialog in neutral state — let user pick approve or reject
        setActionTask(target);
        setActionType(target.task_type === "approval" || target.task_type === "meeting_request" ? "approved" : "done");
        setNotes("");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdParam, tasks]);

  const handleSubmit = async (overrideOutcome?: typeof actionType) => {
    if (!actionTask) return;
    const outcome = overrideOutcome ?? actionType;
    setSubmitting(true);
    try {
      await apiCompleteWorkflowTask(actionTask.id, { outcome, notes: notes || undefined });
      toast({ title: t("myTasks.actionCompleted") });
      setActionTask(null);
      qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
    } catch (err: any) {
      toast({ title: t("workflows.error"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const switchTab = (tab: "assigned" | "submitted") => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "submitted") {
      next.set("tab", "submitted");
    } else {
      next.delete("tab");
    }
    next.delete("taskId");
    setSearchParams(next, { replace: true });
  };

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <PageHeader
          title={t("myTasks.title")}
          subtitle={t("myTasks.subtitle")}
          icon={ListTodo}
          iconColor="text-violet-600"
          iconBg="bg-violet-500/10"
          className="mb-6"
        />

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-border">
          <button
            onClick={() => switchTab("assigned")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === "assigned"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              {t("myTasks.assignedToMe")}
              {pendingTasks.length > 0 && (
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {pendingTasks.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => switchTab("submitted")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === "submitted"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              {t("myTasks.mySubmissions")}
              {submittedTasks.filter((t) => t.status === "pending").length > 0 && (
                <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                  {submittedTasks.filter((t) => t.status === "pending").length}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Timesheet approvals (line managers / HR / admin) */}
        {activeTab === "assigned" && canReviewTimesheets && (
          <div className="mb-8">
            <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {t("timekeeping.myTasksApprovals", { defaultValue: "Timesheet approvals" })}
            </h2>
            <ApprovalsPanel />
          </div>
        )}

        {/* Assigned to Me tab */}
        {activeTab === "assigned" && (
          <>
            {isLoading ? (
              <ListSkeleton rows={4} />
            ) : pendingTasks.length === 0 && completedTasks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">{t("myTasks.noTasks")}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">{t("myTasks.noTasksHint")}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {pendingTasks.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("myTasks.pendingTasks")} ({pendingTasks.length})
                    </h2>
                    <div className="space-y-4">
                      {pendingTasks.map((task) => (
                        <PendingTaskCard
                          key={task.id}
                          task={task}
                          onApprove={() => openAction(task, task.task_type === "meeting_request" ? "accepted" : "approved")}
                          onReject={() => openAction(task, task.task_type === "meeting_request" ? "declined" : "rejected")}
                          onComplete={() => openAction(task, "done")}
                          onSchedule={() => setScheduleTask(task)}
                          onFeedback={() => setFeedbackTask(task)}
                          onReview={() => setReviewTask(task)}
                          onGdpr={() => setGdprTask(task)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {completedTasks.length > 0 && (
                  <CompletedTasksList tasks={completedTasks} />
                )}
              </>
            )}
          </>
        )}

        {/* My Submissions tab */}
        {activeTab === "submitted" && (
          <>
            {isLoadingSubmitted ? (
              <ListSkeleton rows={3} />
            ) : submittedTasks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Send className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">{t("myTasks.noSubmissions", "No submitted requests yet.")}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">{t("myTasks.noSubmissionsHint", "Salary increase requests you submit will appear here.")}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {submittedTasks.filter((t) => t.status === "pending").length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("myTasks.pendingReview", "Pending Review")} ({submittedTasks.filter((t) => t.status === "pending").length})
                    </h2>
                    <div className="space-y-3">
                      {submittedTasks.filter((t) => t.status === "pending").map((task) => (
                        <SubmittedTaskCard key={task.id} task={task} />
                      ))}
                    </div>
                  </div>
                )}

                {submittedTasks.filter((t) => t.status !== "pending").length > 0 && (
                  <div>
                    <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      {t("myTasks.reviewed", "Reviewed")} ({submittedTasks.filter((t) => t.status !== "pending").length})
                    </h2>
                    <div className="space-y-2">
                      {submittedTasks.filter((t) => t.status !== "pending").map((task) => (
                        <SubmittedTaskCard
                          key={task.id}
                          task={task}
                          onArchive={() => archiveSubmittedMutation.mutate(task.id)}
                          onDelete={() => deleteSubmittedMutation.mutate(task.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Archived submissions */}
                <div className="mt-4 pt-4 border-t border-border/50">
                  <button
                    onClick={() => setShowArchivedSubmitted((v) => !v)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    {t(showArchivedSubmitted ? "pilot.task.hideSubmissions" : "pilot.task.showSubmissions")}
                    {showArchivedSubmitted && archivedSubmittedTasks.length > 0 && (
                      <span className="text-[10px] font-medium">({archivedSubmittedTasks.length})</span>
                    )}
                  </button>
                  {showArchivedSubmitted && (
                    <div className="mt-3 space-y-2">
                      {isLoadingArchivedSubmitted ? (
                        <p className="text-xs text-muted-foreground py-2">Loading…</p>
                      ) : archivedSubmittedTasks.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No archived submissions.</p>
                      ) : (
                        archivedSubmittedTasks.map((task) => (
                          <div
                            key={task.id}
                            className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-border/50 bg-muted/20 opacity-60 hover:opacity-100 transition-opacity"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-foreground truncate">{workflowTaskTitle(task.title, t)}</p>
                              <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                                {new Date(task.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2.5 text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                              title="Restore to submissions list"
                              onClick={() => unarchiveSubmittedMutation.mutate(task.id)}
                              disabled={unarchiveSubmittedMutation.isPending}
                            >
                              <RotateCcw className="h-3 w-3" />
                              Restore
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* GDPR candidate data deletion dialog */}
        {gdprTask && (
          <GdprDeletionDialog
            task={gdprTask}
            onClose={(refresh) => {
              setGdprTask(null);
              if (refresh) qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
            }}
          />
        )}

        {/* HR interview review & decision dialog */}
        {reviewTask && (
          <HrInterviewReviewDialog
            task={reviewTask}
            onClose={(refresh) => {
              setReviewTask(null);
              if (refresh) qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
            }}
          />
        )}

        {/* Interview feedback dialog */}
        {feedbackTask && (
          <InterviewFeedbackDialog
            task={feedbackTask}
            onClose={(refresh) => {
              setFeedbackTask(null);
              if (refresh) qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
            }}
          />
        )}

        {/* Interview scheduling dialog */}
        {scheduleTask && (
          <ScheduleInterviewDialog
            task={scheduleTask}
            onClose={(refresh) => {
              setScheduleTask(null);
              if (refresh) qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
            }}
          />
        )}

        {/* Action confirmation dialog */}
        <Dialog open={!!actionTask} onOpenChange={(v) => !v && setActionTask(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{actionTask?.title}</DialogTitle>
              {actionTask?.description && (
                <DialogDescription>{actionTask.description}</DialogDescription>
              )}
            </DialogHeader>
            <div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("myTasks.notesPlaceholder")}
                rows={3}
              />
            </div>
            <DialogFooter className="flex gap-2 sm:justify-between">
              <Button variant="outline" onClick={() => setActionTask(null)} disabled={submitting}>
                {t("workflows.cancel")}
              </Button>
              {(actionTask?.task_type === "approval" || actionTask?.task_type === "meeting_request") ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => handleSubmit(actionTask.task_type === "meeting_request" ? "declined" : "rejected")}
                    disabled={submitting}
                  >
                    <ThumbsDown className="w-3.5 h-3.5 mr-1" />
                    {actionTask.task_type === "meeting_request" ? "Decline" : t("myTasks.reject")}
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleSubmit(actionTask.task_type === "meeting_request" ? "accepted" : "approved")}
                    disabled={submitting}
                  >
                    <ThumbsUp className="w-3.5 h-3.5 mr-1" />
                    {submitting ? t("myTasks.submitting") : actionTask.task_type === "meeting_request" ? "Accept" : t("myTasks.approve")}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                  className={actionType === "rejected" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                >
                  {submitting ? t("myTasks.submitting") : actionType === "rejected" ? t("myTasks.reject") : t("myTasks.complete")}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

// ─── Generate Document button ──────────────────────────────────────────────────

function GenerateDocumentButton({
  templateId,
  employeeId,
}: {
  templateId: number;
  employeeId: number;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const generateMutation = useMutation({
    mutationFn: () =>
      apiGenerateDocumentsFromTemplates({ employeeId, templateIds: [templateId] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast({ title: "Document generated", description: "The document is now available in HR Documents." });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="mt-2">
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-3 text-xs gap-1.5 border-indigo-500/30 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/30"
        disabled={generateMutation.isPending}
        onClick={() => generateMutation.mutate()}
      >
        {generateMutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileText className="h-3.5 w-3.5" />
        )}
        Generate Document
      </Button>
    </div>
  );
}

// ─── Pending task card with expandable employee profile ────────────────────────

function PendingTaskCard({
  task, onApprove, onReject, onComplete, onSchedule, onFeedback, onReview, onGdpr,
}: {
  task: WorkflowTask;
  onApprove: () => void;
  onReject: () => void;
  onComplete: () => void;
  onSchedule: () => void;
  onFeedback: () => void;
  onReview: () => void;
  onGdpr: () => void;
}) {
  const { t, i18n } = useTranslation();
  const tenantSlug = getTenantSlug();
  const hasEmployee = !!(task.context?.employeeId);
  const [showCard, setShowCard] = useState(false);

  const { data: empCard, isLoading: loadingCard } = useQuery<TaskEmployeeCard>({
    queryKey: ["task-employee-card", tenantSlug, task.id],
    queryFn: () => apiGetTaskEmployeeCard(task.id),
    enabled: showCard && hasEmployee,
    staleTime: 60_000,
  });

  const employeeName: string | undefined = task.context?.employeeName;
  const interviewVars = {
    candidateName: task.context?.candidateName ?? "",
    positionTitle: task.context?.positionTitle ?? "",
    interviewerName: task.context?.interviewerName ?? "",
  };
  const taskTitle = task.task_type === "interview_schedule" && task.context?.candidateName
    ? t("myTasks.interviewScheduling.taskTitle", interviewVars)
    : task.task_type === "interview_feedback" && task.context?.candidateName
    ? t("myTasks.interviewTasks.feedbackTaskTitle", interviewVars)
    : task.task_type === "interview_review" && task.context?.candidateName
    ? t("myTasks.interviewTasks.reviewTaskTitle", interviewVars)
    : workflowTaskTitle(task.title, t);
  const taskDescription = task.task_type === "interview_schedule" && task.context?.candidateName
    ? t("myTasks.interviewScheduling.taskDescription", interviewVars)
    : task.task_type === "interview_feedback" && task.context?.candidateName
    ? t("myTasks.interviewTasks.feedbackTaskDescription", interviewVars)
    : task.task_type === "interview_review" && task.context?.candidateName
    ? t("myTasks.interviewTasks.reviewTaskDescription", {
        ...interviewVars,
        outcome: t(`myTasks.interviewTasks.outcomes.${task.context?.outcome ?? "reviewed"}`),
      })
    : workflowTaskDescription(task.description, t);

  const isLeaveTask =
    task.entity_type === "time_off" &&
    !!task.context?.employeeId &&
    !!task.context?.startDate &&
    !!task.context?.endDate;

  const { data: teamOverlap, isLoading: loadingOverlap } = useQuery<TeamLeaveOverlap>({
    queryKey: ["team-leave-overlap", tenantSlug, task.context?.employeeId, task.context?.startDate, task.context?.endDate],
    queryFn: () =>
      apiGetTeamLeaveOverlap(
        task.context!.employeeId as number,
        task.context!.startDate as string,
        task.context!.endDate as string,
      ),
    enabled: isLeaveTask,
    staleTime: 60_000,
  });

  return (
    <Card className="border-l-2 border-l-amber-500/50">
      <CardContent className="py-4 px-5">
        {/* Task header row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-foreground">{taskTitle}</p>
              <Badge variant="outline" className="text-[10px] h-5">
                {task.task_type === "approval" ? "Approval" :
                 task.task_type === "task" ? "Task" :
                 task.task_type === "meeting_request" ? "Meeting" :
                 task.task_type === "interview_schedule" ? t("myTasks.interviewScheduling.interviewBadge") :
                 task.task_type === "interview_feedback" ? t("myTasks.interviewTasks.feedbackBadge") :
                 task.task_type === "interview_review" ? t("myTasks.interviewTasks.reviewBadge") :
                 task.task_type === "gdpr_deletion" ? "GDPR" :
                 task.task_type}
              </Badge>
            </div>
            {taskDescription && (
              <p className="text-xs text-muted-foreground mb-2">{taskDescription}</p>
            )}
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
              {task.workflow_name && <span>{t("myTasks.workflow")}: {task.workflow_name}</span>}
              {/* Salary increase context */}
              {task.context?.baseSalary && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  New salary: {Number(task.context.baseSalary).toLocaleString()} {task.context.currency || "EUR"}
                </span>
              )}
              {/* Expense approval context */}
              {task.context?.expenseId && task.context?.title && (
                <span className="flex items-center gap-1 font-medium text-foreground/80">
                  <FileText className="w-3 h-3" /> {task.context.title}
                </span>
              )}
              {task.context?.expenseId && task.context?.amount && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {Number(task.context.amount).toLocaleString()} {task.context.currency || ""}
                  {task.context.category && ` · ${task.context.category}`}
                </span>
              )}
              {/* Leave approval context */}
              {task.context?.leaveType && (
                <span className="flex items-center gap-1 font-medium text-foreground/80">
                  <Calendar className="w-3 h-3" /> {task.context.leaveType}
                </span>
              )}
              {task.context?.startDate && task.context?.endDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(task.context.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  {task.context.startDate !== task.context.endDate && ` – ${new Date(task.context.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                  {task.context.workingDays && ` (${task.context.workingDays}d)`}
                </span>
              )}
              {task.context?.goalTitle && (
                <span>Goal: {task.context.goalTitle}</span>
              )}
              {(task.task_type === "interview_schedule" || task.task_type === "interview_feedback") && task.context?.candidateName && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {task.context.candidateName}
                </span>
              )}
              {(task.task_type === "interview_schedule" || task.task_type === "interview_feedback") && task.context?.positionTitle && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> {task.context.positionTitle}
                </span>
              )}
              {task.task_type === "interview_feedback" && task.context?.scheduledAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(task.context.scheduledAt).toLocaleDateString(i18n.resolvedLanguage === "mk" ? "mk-MK" : "en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {task.task_type === "interview_review" && task.context?.outcome && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  task.context.outcome === "passed" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                  task.context.outcome === "failed" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
                  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                }`}>
                  {task.context.outcome === "passed" ? `✅ ${t("myTasks.interviewTasks.outcomes.passed")}` : task.context.outcome === "failed" ? `❌ ${t("myTasks.interviewTasks.outcomes.failed")}` : `📋 ${t("myTasks.interviewTasks.outcomes.reviewed")}`}
                </span>
              )}
              {task.task_type === "gdpr_deletion" && task.context?.candidateName && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" /> {task.context.candidateName}
                </span>
              )}
              {task.task_type === "gdpr_deletion" && task.context?.positionTitle && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> {task.context.positionTitle}
                </span>
              )}
              {task.task_type === "gdpr_deletion" && task.due_date && (
                <span className="flex items-center gap-1 text-red-500 dark:text-red-400 font-medium">
                  <ShieldAlert className="w-3 h-3" /> Due: {new Date(task.due_date).toLocaleDateString("en-GB")}
                </span>
              )}
              <span>{new Date(task.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 ml-2 shrink-0">
            {task.task_type === "approval" ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-500 dark:text-red-400 border-red-500/20 hover:bg-red-500/10"
                  onClick={onReject}
                >
                  <ThumbsDown className="w-3.5 h-3.5 mr-1" /> {t("myTasks.reject")}
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={onApprove}
                >
                  <ThumbsUp className="w-3.5 h-3.5 mr-1" /> {t("myTasks.approve")}
                </Button>
              </>
            ) : task.task_type === "interview_schedule" ? (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={onSchedule}>
                <CalendarCheck className="w-3.5 h-3.5 mr-1" /> {t("myTasks.interviewScheduling.scheduleInterview")}
              </Button>
            ) : task.task_type === "interview_feedback" ? (
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={onFeedback}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("myTasks.interviewTasks.submitFeedback")}
              </Button>
            ) : task.task_type === "interview_review" ? (
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={onReview}>
                <ChevronRight className="w-3.5 h-3.5 mr-1" /> {t("myTasks.interviewTasks.decideNextStep")}
              </Button>
            ) : task.task_type === "gdpr_deletion" ? (
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={onGdpr}>
                <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Review & Delete
              </Button>
            ) : task.task_type === "meeting_request" ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-500 dark:text-red-400 border-red-500/20 hover:bg-red-500/10"
                  onClick={onReject}
                >
                  <CalendarX className="w-3.5 h-3.5 mr-1" /> Decline
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={onApprove}
                >
                  <CalendarCheck className="w-3.5 h-3.5 mr-1" /> Accept
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={onComplete}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t("myTasks.complete")}
              </Button>
            )}
          </div>
        </div>

        {/* Generate Document button — shown when step has a linked template */}
        {task.context?.documentTemplateId && task.context?.employeeId && (
          <GenerateDocumentButton
            templateId={task.context.documentTemplateId as number}
            employeeId={task.context.employeeId as number}
          />
        )}

        {/* Employee profile chip — clickable, sits below the task meta */}
        {hasEmployee && employeeName && (
          <button
            onClick={() => setShowCard((v) => !v)}
            className={`mt-3 w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-all ${
              showCard
                ? "border-primary/40 bg-primary/5 text-foreground"
                : "border-border bg-muted/30 text-muted-foreground hover:border-primary/30 hover:bg-primary/5 hover:text-foreground"
            }`}
          >
            {/* Avatar initial */}
            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
              {employeeName.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <span className="text-xs font-medium flex-1">{employeeName}</span>
            <span className="text-[10px] text-muted-foreground mr-1">
              {showCard ? "Hide profile" : "View profile"}
            </span>
            {showCard ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
          </button>
        )}

        {/* Expandable employee card */}
        {showCard && hasEmployee && (
          <div className="mt-3">
            {loadingCard ? (
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <div className="flex items-center gap-3 animate-pulse">
                  <div className="w-12 h-12 rounded-full bg-muted" />
                  <div className="space-y-1.5">
                    <div className="h-3 w-32 bg-muted rounded" />
                    <div className="h-2.5 w-24 bg-muted rounded" />
                  </div>
                </div>
              </div>
            ) : empCard ? (
              <EmployeeProfileCard card={empCard} />
            ) : (
              <p className="text-xs text-muted-foreground px-1">Could not load employee profile.</p>
            )}
          </div>
        )}

        {/* Team availability during leave period */}
        {isLeaveTask && (
          <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold text-foreground">Team Availability</span>
            </div>
            {loadingOverlap ? (
              <div className="space-y-1.5 animate-pulse">
                <div className="h-2.5 w-48 bg-muted rounded" />
                <div className="h-2.5 w-36 bg-muted rounded" />
              </div>
            ) : teamOverlap ? (
              <>
                <p className="text-[11px] text-muted-foreground mb-2">
                  {teamOverlap.onLeaveCount === 0
                    ? `All ${teamOverlap.teamSize} teammate${teamOverlap.teamSize !== 1 ? "s" : ""} working during this period`
                    : `${teamOverlap.onLeaveCount} of ${teamOverlap.teamSize} teammate${teamOverlap.teamSize !== 1 ? "s" : ""} on leave (${Math.round((teamOverlap.onLeaveCount / teamOverlap.teamSize) * 100)}%) · ${teamOverlap.workingCount} working`}
                </p>
                {teamOverlap.onLeave.length > 0 && (
                  <div className="space-y-1">
                    {teamOverlap.onLeave.map((entry) => (
                      <div key={entry.employeeId} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="font-medium text-foreground/80 truncate">{entry.employeeName}</span>
                        <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
                          <span>{entry.leaveType}</span>
                          <span>·</span>
                          <span>
                            {new Date(entry.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            {entry.startDate !== entry.endDate &&
                              ` – ${new Date(entry.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                          </span>
                          <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                            entry.status === "Approved"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          }`}>
                            {entry.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Employee profile card ─────────────────────────────────────────────────────

function EmployeeProfileCard({ card }: { card: TaskEmployeeCard }) {
  const { t } = useTranslation();
  const [salaryRevealed, setSalaryRevealed] = useState(false);

  const avatar = card.avatarUrl ? (
    <img src={card.avatarUrl} alt={card.name} className="w-12 h-12 rounded-full object-cover" />
  ) : (
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
      style={{ backgroundColor: card.avatarColor || "#6366f1" }}
    >
      {card.initials}
    </div>
  );

  const managerAvatar = card.reportsTo ? (
    card.reportsTo.avatarUrl ? (
      <img src={card.reportsTo.avatarUrl} alt={card.reportsTo.name} className="w-5 h-5 rounded-full object-cover" />
    ) : (
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-semibold shrink-0"
        style={{ backgroundColor: card.reportsTo.avatarColor || "#6366f1" }}
      >
        {card.reportsTo.initials}
      </div>
    )
  ) : null;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      {/* Top: avatar + name + job title */}
      <div className="flex items-center gap-3 mb-4">
        {avatar}
        <div>
          <p className="text-sm font-semibold text-foreground">{card.name}</p>
          {card.jobTitle && <p className="text-xs text-muted-foreground">{card.jobTitle}</p>}
          <Badge
            variant="outline"
            className={`text-[10px] h-4 mt-0.5 ${
              card.status === "Active"
                ? "border-green-500/30 text-green-600 dark:text-green-400"
                : "border-muted-foreground/30 text-muted-foreground"
            }`}
          >
            {card.status}
          </Badge>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-xs">

        {/* Hire date */}
        {card.startDate && (
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Hire Date</p>
              <p className="text-foreground font-medium">
                {new Date(card.startDate).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </p>
            </div>
          </div>
        )}

        {/* Department */}
        {card.department && (
          <div className="flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Department</p>
              <p className="text-foreground font-medium">{card.department}</p>
            </div>
          </div>
        )}

        {/* Team */}
        {card.teamName && (
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Team</p>
              <p className="text-foreground font-medium">{card.teamName}</p>
            </div>
          </div>
        )}

        {/* Employment type */}
        {card.contractType && (
          <div className="flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Employment Type</p>
              <p className="text-foreground font-medium">{getContractTypeLabel(card.contractType, t)}</p>
            </div>
          </div>
        )}

        {/* Line manager */}
        {card.reportsTo && (
          <div className="flex items-center gap-2 col-span-2">
            {managerAvatar}
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Line Manager</p>
              <p className="text-foreground font-medium">{card.reportsTo.name}</p>
            </div>
          </div>
        )}

        {/* Equipment */}
        {card.equipment.length > 0 && (
          <div className="col-span-2 flex items-start gap-2">
            <Monitor className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Equipment</p>
              <div className="flex flex-wrap gap-1">
                {card.equipment.map((eq, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] h-5">
                    {eq.item_name}{eq.serial_number ? ` · ${eq.serial_number}` : ""}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Salary — only for permitted viewers, hidden until clicked */}
        {card.canSeeSalary && card.salary && (
          <div className="col-span-2 mt-1 pt-2.5 border-t border-border/60 flex items-center gap-2">
            <button
              onClick={() => setSalaryRevealed((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              {salaryRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {salaryRevealed ? "Hide Salary" : "Reveal Salary"}
            </button>
            {salaryRevealed && (
              <span className="text-xs font-semibold text-foreground ml-2">
                {Number(card.salary.base_salary).toLocaleString()} {card.salary.currency}
                <span className="font-normal text-muted-foreground ml-1">/ {card.salary.pay_frequency}</span>
                {card.salary.bonus ? (
                  <span className="font-normal text-muted-foreground ml-2">+ {Number(card.salary.bonus).toLocaleString()} bonus</span>
                ) : null}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Completed tasks list + detail dialog ─────────────────────────────────────

const COMPLETED_PAGE_SIZE = 10;

function CompletedTasksList({ tasks }: { tasks: WorkflowTask[] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const tenantSlug = getTenantSlug();
  const [selected, setSelected] = useState<WorkflowTask | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);

  const totalPages = Math.max(1, Math.ceil(tasks.length / COMPLETED_PAGE_SIZE));
  // Clamp page in case tasks shrink after archive/delete
  const safePage = Math.min(page, totalPages);
  const paginatedTasks = tasks.slice((safePage - 1) * COMPLETED_PAGE_SIZE, safePage * COMPLETED_PAGE_SIZE);

  const { data: archivedTasks = [], isLoading: isLoadingArchived } = useQuery<WorkflowTask[]>({
    queryKey: ["workflow-tasks-archived", tenantSlug],
    queryFn: apiGetArchivedWorkflowTasks,
    enabled: showArchived,
    staleTime: 30_000,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => apiArchiveWorkflowTask(id),
    onSuccess: () => {
      toast({ title: "Task archived" });
      qc.invalidateQueries({ queryKey: ["workflow-tasks", tenantSlug] });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-archived", tenantSlug] });
      setSelected(null);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: number) => apiUnarchiveWorkflowTask(id),
    onSuccess: () => {
      toast({ title: "Task restored", description: "The task is back in your completed list." });
      qc.invalidateQueries({ queryKey: ["workflow-tasks", tenantSlug] });
      qc.invalidateQueries({ queryKey: ["workflow-tasks-archived", tenantSlug] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDeleteWorkflowTask(id),
    onSuccess: () => {
      toast({ title: "Task deleted" });
      qc.invalidateQueries({ queryKey: ["workflow-tasks", tenantSlug] });
      setSelected(null);
      setConfirmDelete(false);
      setPendingDeleteId(null);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const outcomeColor = (outcome?: string | null) => {
    if (outcome === "approved") return "border-green-500/40 text-green-600 dark:text-green-400";
    if (outcome === "rejected") return "border-red-500/40 text-red-500 dark:text-red-400";
    return "border-muted-foreground/30 text-muted-foreground";
  };

  const outcomeLabel = (task: WorkflowTask) => {
    if (task.outcome === "approved") return t("pilot.status.approved");
    if (task.outcome === "rejected") return t("pilot.status.rejected");
    if (task.status === "cancelled") return t("pilot.status.cancelled");
    return t("pilot.status.done");
  };

  return (
    <>
      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {t("myTasks.completedTasks")} ({tasks.length})
        </h2>
        <div className="space-y-2">
          {paginatedTasks.map((task) => (
            <Card
              key={task.id}
              className="opacity-70 hover:opacity-100 hover:border-border/80 hover:shadow-sm transition-all group"
              onClick={() => { if (pendingDeleteId !== task.id) setSelected(task); }}
            >
              <CardContent className="py-3 px-5 flex items-center justify-between gap-3 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{workflowTaskTitle(task.title, t)}</p>
                  <p className="text-xs text-muted-foreground/70 mt-0.5">
                    {task.workflow_name}
                    {task.context?.employeeName && <span> · {task.context.employeeName}</span>}
                    {" · "}{new Date(task.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-[10px] h-5 ${outcomeColor(task.outcome)}`}>
                    {outcomeLabel(task)}
                  </Badge>

                  {/* Actions + chevron grouped so chevron sits at position-0 when actions are collapsed */}
                  <div className="flex items-center shrink-0">
                    {pendingDeleteId === task.id ? (
                      <div className="flex items-center gap-1 mr-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="destructive" size="icon"
                          className="h-6 w-6"
                          title="Confirm delete"
                          onClick={() => deleteMutation.mutate(task.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-6 w-6 text-muted-foreground"
                          title="Cancel"
                          onClick={() => setPendingDeleteId(null)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="overflow-hidden flex items-center gap-0.5 w-0 group-hover:w-[3.25rem] transition-[width] duration-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="ghost" size="icon"
                          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                          title={t("pilot.task.archive")}
                          onClick={() => archiveMutation.mutate(task.id)}
                          disabled={archiveMutation.isPending}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-red-500"
                          title={t("pilot.task.delete")}
                          onClick={() => setPendingDeleteId(task.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {(safePage - 1) * COMPLETED_PAGE_SIZE + 1}–{Math.min(safePage * COMPLETED_PAGE_SIZE, tasks.length)} of {tasks.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground px-2 tabular-nums">
                {safePage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Archived tasks section ────────────────────────────────────── */}
      <div className="mt-4 pt-4 border-t border-border/50">
        <button
          onClick={() => setShowArchived((v) => !v)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <Archive className="w-3.5 h-3.5" />
          {t(showArchived ? "pilot.task.hideArchived" : "pilot.task.showArchived")}
          {showArchived && archivedTasks.length > 0 && (
            <span className="text-[10px] font-medium">({archivedTasks.length})</span>
          )}
        </button>

        {showArchived && (
          <div className="mt-3 space-y-2">
            {isLoadingArchived ? (
              <p className="text-xs text-muted-foreground py-2">Loading…</p>
            ) : archivedTasks.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No archived tasks.</p>
            ) : (
              archivedTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-border/50 bg-muted/20 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">{workflowTaskTitle(task.title, t)}</p>
                    <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                      {task.workflow_name}
                      {task.context?.employeeName && <span> · {task.context.employeeName}</span>}
                      {" · "}{new Date(task.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2.5 text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                    title="Restore to completed list"
                    onClick={() => unarchiveMutation.mutate(task.id)}
                    disabled={unarchiveMutation.isPending}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setConfirmDelete(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="pr-6">{selected ? workflowTaskTitle(selected.title, t) : ""}</DialogTitle>
            <DialogDescription>
              {selected?.workflow_name}
              {selected?.context?.employeeName && <span> · {selected.context.employeeName}</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {/* Outcome badge */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">Outcome:</span>
              <Badge variant="outline" className={`text-[10px] h-5 ${outcomeColor(selected?.outcome)}`}>
                {selected ? outcomeLabel(selected) : ""}
              </Badge>
            </div>

            {/* Task type */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">Type:</span>
              <Badge variant="outline" className="text-[10px] h-5">{selected?.task_type}</Badge>
            </div>

            {/* Description */}
            {selected?.description && (
              <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground leading-relaxed">
                {workflowTaskDescription(selected.description, t)}
              </div>
            )}

            {/* Context details */}
            {(selected?.context?.baseSalary || selected?.context?.reason || selected?.context?.leaveType) && (
              <div className="rounded-md border border-border p-3 space-y-1.5 text-xs">
                {selected?.context?.baseSalary && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Salary</span>
                    <span className="font-medium">{Number(selected.context.baseSalary).toLocaleString()} {selected.context.currency || "EUR"}</span>
                  </div>
                )}
                {selected?.context?.leaveType && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Leave type</span>
                    <span className="font-medium">{selected.context.leaveType}</span>
                  </div>
                )}
                {selected?.context?.durationDays && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{selected.context.durationDays} days</span>
                  </div>
                )}
                {selected?.context?.reason && (
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground shrink-0">Reason</span>
                    <span className="font-medium text-right">{selected.context.reason}</span>
                  </div>
                )}
              </div>
            )}

            {/* Dates */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Created: {selected && new Date(selected.created_at).toLocaleDateString()}</span>
              {selected?.completed_at && (
                <span>Completed: {new Date(selected.completed_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>

          {/* Confirm delete prompt */}
          {confirmDelete && (
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-600 dark:text-red-400">
              This will permanently delete the task record. This cannot be undone.
            </div>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-between sm:items-center">
            <div className="flex gap-2">
              {!confirmDelete ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-muted-foreground gap-1.5"
                    onClick={() => selected && archiveMutation.mutate(selected.id)}
                    disabled={archiveMutation.isPending}
                  >
                    <Archive className="w-3.5 h-3.5" />{t("pilot.task.archive")} </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 dark:text-red-400 border-red-500/20 hover:bg-red-500/10 gap-1.5"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />{t("pilot.task.delete")} </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => selected && deleteMutation.mutate(selected.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    {deleteMutation.isPending ? "Deleting…" : "Confirm Delete"}
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => { setSelected(null); setConfirmDelete(false); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Submitted task card ───────────────────────────────────────────────────────

// ─── HR interview review & decision dialog ────────────────────────────────────

function HrInterviewReviewDialog({ task, onClose }: { task: WorkflowTask; onClose: (refresh?: boolean) => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const ctx = task.context ?? {};
  const [hrNotes, setHrNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const outcomeColor = ctx.outcome === "passed"
    ? "bg-green-50 border-green-300 text-green-700 dark:bg-green-950/40 dark:border-green-700 dark:text-green-400"
    : ctx.outcome === "failed"
    ? "bg-red-50 border-red-300 text-red-700 dark:bg-red-950/40 dark:border-red-700 dark:text-red-400"
    : "bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-950/40 dark:border-amber-700 dark:text-amber-400";

  const DECISIONS: { value: "offer" | "reject" | "another_round" | "keep"; label: string; desc: string; color: string }[] = [
    { value: "offer",         label: t("myTasks.interviewTasks.decisions.offer.label"),         desc: t("myTasks.interviewTasks.decisions.offer.description"),         color: "border-green-400 bg-green-50 hover:bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300 dark:border-green-700" },
    { value: "another_round", label: t("myTasks.interviewTasks.decisions.anotherRound.label"),  desc: t("myTasks.interviewTasks.decisions.anotherRound.description"),  color: "border-blue-400 bg-blue-50 hover:bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-700" },
    { value: "keep",          label: t("myTasks.interviewTasks.decisions.keep.label"),          desc: t("myTasks.interviewTasks.decisions.keep.description"),          color: "border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700" },
    { value: "reject",        label: t("myTasks.interviewTasks.decisions.reject.label"),        desc: t("myTasks.interviewTasks.decisions.reject.description"),        color: "border-red-400 bg-red-50 hover:bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 dark:border-red-700" },
  ];

  const handleDecision = async (decision: "offer" | "reject" | "another_round" | "keep") => {
    if (!ctx.applicationId) return;
    setSubmitting(true);
    try {
      await apiHrInterviewDecision(ctx.applicationId, { decision, hrNotes: hrNotes.trim() || undefined });
      toast({
        title: t("myTasks.interviewTasks.decisionRecorded"),
        description: t("myTasks.interviewTasks.decisionRecordedDescription", {
          decision: t(`myTasks.interviewTasks.decisionResults.${decision}`),
        }),
      });
      onClose(true);
    } catch (err: any) {
      toast({ title: t("myTasks.interviewTasks.failed"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChevronRight className="w-5 h-5 text-orange-600" />
            {t("myTasks.interviewTasks.reviewDialogTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("myTasks.interviewTasks.reviewDialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Candidate + feedback summary */}
          <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5">
            <p className="text-sm font-medium">{ctx.candidateName}</p>
            {ctx.positionTitle && <p className="text-xs text-muted-foreground">{ctx.positionTitle}</p>}
            {ctx.interviewerName && <p className="text-xs text-muted-foreground">{t("myTasks.interviewTasks.interviewedBy", { interviewerName: ctx.interviewerName })}</p>}
          </div>

          {/* Outcome badge + feedback notes */}
          <div className={`rounded-lg border p-3 space-y-1.5 ${outcomeColor}`}>
            <p className="text-xs font-semibold uppercase tracking-wide">
              {ctx.outcome === "passed" ? `✅ ${t("myTasks.interviewTasks.outcomes.passed")}` : ctx.outcome === "failed" ? `❌ ${t("myTasks.interviewTasks.outcomes.failed")}` : `📋 ${t("myTasks.interviewTasks.outcomes.reviewed")}`}
            </p>
            {ctx.feedbackNotes
              ? <p className="text-sm">{ctx.feedbackNotes}</p>
              : <p className="text-sm opacity-60 italic">{t("myTasks.interviewTasks.noFeedbackNotes")}</p>
            }
          </div>

          {/* HR notes */}
          <div>
            <p className="text-sm font-medium mb-1">
              {t("myTasks.interviewTasks.hrNotes")}
              <span className="text-muted-foreground font-normal text-xs ml-1">{t("myTasks.interviewTasks.hrNotesOptional")}</span>
            </p>
            <Textarea
              value={hrNotes}
              onChange={(e) => setHrNotes(e.target.value)}
              placeholder={t("myTasks.interviewTasks.hrNotesPlaceholder")}
              rows={3}
            />
          </div>

          {/* Decision buttons */}
          <div>
            <p className="text-sm font-medium mb-2">{t("myTasks.interviewTasks.action")} <span className="text-red-500">*</span></p>
            <div className="grid grid-cols-2 gap-2">
              {DECISIONS.map((d) => (
                <button
                  key={d.value}
                  disabled={submitting}
                  onClick={() => handleDecision(d.value)}
                  className={`rounded-lg border-2 p-3 text-left transition-all disabled:opacity-50 ${d.color}`}
                >
                  <p className="text-sm font-semibold">{d.label}</p>
                  <p className="text-xs opacity-70 mt-0.5">{d.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()} disabled={submitting}>{t("myTasks.interviewTasks.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Interview feedback dialog ────────────────────────────────────────────────

function InterviewFeedbackDialog({ task, onClose }: { task: WorkflowTask; onClose: (refresh?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const ctx = task.context ?? {};
  const [outcome, setOutcome] = useState<"passed" | "failed" | "reviewed">("reviewed");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadingResumeAppId, setDownloadingResumeAppId] = useState<number | null>(null);

  const OUTCOMES: { value: "passed" | "failed" | "reviewed"; label: string; color: string; bg: string }[] = [
    { value: "passed",   label: t("myTasks.interviewTasks.outcomes.passed"),   color: "text-green-700 dark:text-green-400",  bg: "border-green-400 bg-green-50 dark:bg-green-950/40" },
    { value: "reviewed", label: t("myTasks.interviewTasks.outcomes.reviewed"), color: "text-amber-700 dark:text-amber-400",  bg: "border-amber-400 bg-amber-50 dark:bg-amber-950/40" },
    { value: "failed",   label: t("myTasks.interviewTasks.outcomes.failed"),   color: "text-red-700 dark:text-red-400",      bg: "border-red-400 bg-red-50 dark:bg-red-950/40" },
  ];

  const handleSubmit = async () => {
    if (!ctx.applicationId || !ctx.interviewId) return;
    setSubmitting(true);
    try {
      await apiSubmitInterviewFeedback(ctx.applicationId, ctx.interviewId, {
        outcome,
        notes: notes.trim() || undefined,
      });
      toast({ title: t("myTasks.interviewTasks.feedbackSubmitted"), description: t("myTasks.interviewTasks.hrNotified") });
      onClose(true);
    } catch (err: any) {
      toast({ title: t("myTasks.interviewTasks.feedbackFailed"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResumeDownload = async (applicationId: number) => {
    setDownloadingResumeAppId(applicationId);
    try {
      await apiDownloadRecruitmentResume(applicationId);
    } catch (err: any) {
      toast({
        title: t("myTasks.interviewTasks.downloadFailed"),
        description: err?.message ?? t("myTasks.interviewTasks.downloadFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setDownloadingResumeAppId(null);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-violet-600" />
            {t("myTasks.interviewTasks.feedbackDialogTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("myTasks.interviewTasks.feedbackDialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Candidate profile card */}
          <div className="rounded-lg border bg-muted/40 p-3 space-y-2.5">
            <div>
              <p className="text-sm font-semibold">{ctx.candidateName}</p>
              {ctx.positionTitle && <p className="text-xs text-muted-foreground">{ctx.positionTitle}</p>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* Interview time */}
              {ctx.scheduledAt && (
                <div className="flex items-start gap-1.5 col-span-2">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t("myTasks.interviewScheduling.interviewTime")}</p>
                    <p className="text-xs font-medium text-foreground">
                      {new Date(ctx.scheduledAt).toLocaleString(i18n.resolvedLanguage === "mk" ? "mk-MK" : "en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              )}

              {/* Candidate email */}
              {ctx.candidateEmail && (
                <div className="flex items-start gap-1.5 col-span-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t("myTasks.interviewTasks.candidateEmail")}</p>
                    <p className="text-xs font-medium text-foreground">{ctx.candidateEmail}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action links — Meet + CV */}
            <div className="flex flex-wrap gap-2 pt-0.5">
              {ctx.meetLink && (
                <a
                  href={ctx.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
                >
                  <Video className="w-3.5 h-3.5" />
                  {t("myTasks.interviewTasks.joinMeeting")}
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </a>
              )}
              {ctx.hasResume && ctx.applicationId && (
                <button
                  type="button"
                  onClick={() => handleResumeDownload(ctx.applicationId!)}
                  disabled={downloadingResumeAppId === ctx.applicationId}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border hover:bg-muted/60 text-xs font-medium text-foreground transition-colors"
                >
                  {downloadingResumeAppId === ctx.applicationId ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5" />
                  )}
                  {t("myTasks.interviewTasks.downloadCv")}
                </button>
              )}
            </div>
          </div>

          {/* Outcome selection */}
          <div>
            <p className="text-sm font-medium mb-2">{t("myTasks.interviewTasks.outcome")} <span className="text-red-500">*</span></p>
            <div className="grid grid-cols-3 gap-2">
              {OUTCOMES.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setOutcome(o.value)}
                  className={`rounded-lg border-2 py-2.5 text-sm font-medium transition-all ${
                    outcome === o.value ? `${o.bg} ${o.color}` : "border-border bg-background text-muted-foreground hover:border-border/60"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-sm font-medium mb-1">
              {t("myTasks.interviewTasks.notesObservations")}
              <span className="text-muted-foreground font-normal text-xs ml-1">{t("myTasks.interviewTasks.optional")}</span>
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("myTasks.interviewTasks.notesPlaceholder")}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>{t("myTasks.interviewTasks.cancel")}</Button>
          <Button
            disabled={submitting}
            onClick={handleSubmit}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? t("myTasks.interviewTasks.submitting") : t("myTasks.interviewTasks.submitNotifyHr")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Interview scheduling dialog ──────────────────────────────────────────────

function ScheduleInterviewDialog({ task, onClose }: { task: WorkflowTask; onClose: (refresh?: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const dateLocale = useDateLocale();
  const { toast } = useToast();
  const qc = useQueryClient();
  const ctx = task.context ?? {};
  const applicationId: number | undefined = ctx.applicationId;
  const interviewId: number | undefined = ctx.interviewId;

  // Use company timezone for slot display and sending
  const { data: companyData } = useQuery({
    queryKey: ["company-details"],
    queryFn: apiGetCompanyDetails,
  });

  // ── Tab: Schedule Directly ──────────────────────────────────────────────────
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generateLinkError, setGenerateLinkError] = useState<string | null>(null);

  const handleSchedule = async () => {
    if (!date || !applicationId || !interviewId) return;
    const [h, m] = time.split(":").map(Number);
    const scheduledAt = new Date(date);
    scheduledAt.setHours(h, m, 0, 0);
    setSubmitting(true);
    try {
      await apiScheduleInterview(applicationId, interviewId, {
        scheduledAt: scheduledAt.toISOString(),
        location: location.trim() || undefined,
        meetingLink: meetingLink.trim() || undefined,
      });
      toast({
        title: t("myTasks.interviewScheduling.scheduledToast"),
        description: t("myTasks.interviewScheduling.invitationSentToast", { candidate: ctx.candidateEmail || ctx.candidateName }),
      });
      onClose(true);
    } catch (err: any) {
      toast({ title: t("myTasks.interviewScheduling.scheduleFailed"), description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!date) { toast({ title: t("myTasks.interviewScheduling.selectDateTimeFirst"), variant: "destructive" }); return; }
    setIsGeneratingLink(true);
    setGenerateLinkError(null);
    try {
      const [h, m] = time.split(":").map(Number);
      const start = new Date(date);
      start.setHours(h, m, 0, 0);
      const end = new Date(start.getTime() + 60 * 60_000); // default 1 hour
      const title = [
        t("myTasks.interviewScheduling.interviewBadge"),
        ctx.candidateName && `— ${ctx.candidateName}`,
        ctx.positionTitle && `(${ctx.positionTitle})`,
      ].filter(Boolean).join(" ");
      const { meetLink } = await apiCreateCalendarMeeting({
        title,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
      });
      setMeetingLink(meetLink);
      toast({ title: t("myTasks.interviewScheduling.linkGenerated"), description: t("myTasks.interviewScheduling.linkGeneratedDescription") });
    } catch (err: any) {
      if (err.message?.includes("insufficient_scope") || err.error === "insufficient_scope") {
        setGenerateLinkError("reconnect");
      } else {
        toast({ title: t("myTasks.interviewScheduling.linkGenerationFailed"), description: err.message, variant: "destructive" });
      }
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // ── Tab: Offer Slots via Calendar ───────────────────────────────────────────
  const { data: calendarStatus, isLoading: calendarLoading } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: apiGetCalendarStatus,
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [slotDuration, setSlotDuration] = useState(60);
  const [fetchedSlots, setFetchedSlots] = useState<FreeSlot[] | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const [isFetching, setIsFetching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  // Use the company timezone (from Organization settings) so slots are displayed
  // and sent in the company's local time, not the interviewer's browser timezone.
  const timezone = companyData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleFetchSlots = async () => {
    if (!startDate || !endDate) { toast({ title: t("myTasks.interviewScheduling.selectDateRangeFirst"), variant: "destructive" }); return; }
    setIsFetching(true);
    try {
      const { slots } = await apiGetFreeSlots(startDate, endDate, slotDuration, timezone);
      setFetchedSlots(slots);
      setSelectedSlots(new Set(slots.map((s) => s.startsAt)));
    } catch (err: any) {
      toast({ title: t("myTasks.interviewScheduling.fetchSlotsFailed"), description: err.message, variant: "destructive" });
    } finally {
      setIsFetching(false);
    }
  };

  const handleSendSlots = async () => {
    if (!fetchedSlots || !applicationId || !interviewId) return;
    const confirmed = fetchedSlots.filter((s) => selectedSlots.has(s.startsAt));
    if (confirmed.length === 0) { toast({ title: t("myTasks.interviewScheduling.selectOneSlot"), variant: "destructive" }); return; }
    // Map each individually selected slot to its own window so generateSlots
    // on the backend creates exactly ONE slot per window — no gap-filling.
    // (Collapsing all slots per day into a first→last window would regenerate
    // all intermediate slots even if the HR deliberately skipped them.)
    const windows = confirmed.map((s) => ({
      date: s.date,
      startTime: new Date(s.startsAt).toISOString().slice(11, 16),
      endTime: new Date(s.endsAt).toISOString().slice(11, 16),
    }));
    setIsSending(true);
    try {
      const result = await apiSetInterviewAvailability(applicationId, interviewId, {
        windows,
        slotDurationMinutes: slotDuration,
        timezone,
      });
      setBookingUrl(result.bookingUrl);
      toast({ title: t("myTasks.interviewScheduling.slotsSentToast", { count: result.totalSlots }) });
      // Mark the scheduling task as complete so it moves out of the pending inbox
      try {
        await apiCompleteWorkflowTask(task.id, { outcome: "sent" });
      } catch {
        // Non-fatal — task may already be completed or not workflow-driven
      }
      qc.invalidateQueries({ queryKey: ["workflow-tasks"] });
      onClose(true);
    } catch (err: any) {
      toast({ title: t("myTasks.interviewScheduling.sendAvailabilityFailed"), description: err.message, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const toggleSlot = (startsAt: string) => {
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(startsAt)) next.delete(startsAt); else next.add(startsAt);
      return next;
    });
  };

  const grouped: Record<string, FreeSlot[]> = {};
  for (const s of fetchedSlots ?? []) { if (!grouped[s.date]) grouped[s.date] = []; grouped[s.date].push(s); }
  // Display in the user's local timezone (same tz used to compute free slots on the server).
  const browserLocale = i18n.language === "mk" ? "mk-MK" : "en-GB";
  const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString(browserLocale, { hour: "2-digit", minute: "2-digit", timeZone: timezone });
  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(browserLocale, { weekday: "short", day: "numeric", month: "short", timeZone: timezone });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-blue-600" />
            {t("myTasks.interviewScheduling.scheduleInterview")}
          </DialogTitle>
          <DialogDescription>
            {t("myTasks.interviewScheduling.description")}
          </DialogDescription>
        </DialogHeader>

        {/* Candidate info card */}
        <div className="rounded-lg border bg-muted/40 p-3 space-y-0.5">
          <p className="text-sm font-medium">{ctx.candidateName}</p>
          {ctx.candidateEmail && <p className="text-xs text-muted-foreground">{ctx.candidateEmail}</p>}
          {ctx.positionTitle && <p className="text-xs text-muted-foreground">{ctx.positionTitle}</p>}
        </div>

        <Tabs defaultValue="slots" className="mt-1">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="slots" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <CalendarDays className="w-3.5 h-3.5" />{t("myTasks.interviewScheduling.offerSlots")}
            </TabsTrigger>
            <TabsTrigger value="direct" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <CalendarCheck className="w-3.5 h-3.5" />{t("myTasks.interviewScheduling.scheduleDirectly")}
            </TabsTrigger>
          </TabsList>

          {/* ── Offer Slots tab ─────────────────────────────────────────────── */}
          <TabsContent value="slots" className="mt-4 space-y-4">
            {calendarLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("myTasks.interviewScheduling.checkingCalendar")}
              </div>
            ) : !calendarStatus?.connected ? (
              /* Not connected — prompt to Settings */
              <div className="rounded-lg border border-dashed border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/10 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-violet-600 shrink-0" />
                  <span className="text-sm font-medium text-violet-700 dark:text-violet-400">{t("myTasks.interviewScheduling.connectCalendarFirst")}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("myTasks.interviewScheduling.connectCalendarDescription")}
                </p>
                <div className="flex gap-2">
                  <a
                    href={getCalendarOAuthUrl("microsoft", "/settings?tab=calendar")}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-accent transition-colors"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="10" height="10" fill="#F25022"/>
                      <rect x="11" width="10" height="10" fill="#7FBA00"/>
                      <rect y="11" width="10" height="10" fill="#00A4EF"/>
                      <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
                    </svg>
                    {t("myTasks.interviewScheduling.connectMicrosoft")}
                  </a>
                  <a
                    href={getCalendarOAuthUrl("google", "/settings?tab=calendar")}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-accent transition-colors"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {t("myTasks.interviewScheduling.connectGoogle")}
                  </a>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("myTasks.interviewScheduling.manageCalendarPrefix")}{" "}
                  <button onClick={() => { window.location.href = "/settings?tab=calendar"; }} className="underline hover:text-foreground transition-colors">{t("myTasks.interviewScheduling.settingsCalendar")}</button>.
                </p>
              </div>
            ) : bookingUrl ? (
              /* Slots already sent */
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{t("myTasks.interviewScheduling.availabilitySent")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={bookingUrl}
                    className="flex-1 text-xs bg-muted border rounded px-2 py-1.5 text-muted-foreground truncate"
                  />
                  <button
                    onClick={() => { navigator.clipboard.writeText(bookingUrl); toast({ title: t("myTasks.interviewScheduling.linkCopied") }); }}
                    className="text-xs text-violet-600 hover:underline whitespace-nowrap"
                  >{t("myTasks.interviewScheduling.copy")}</button>
                </div>
                <button
                  onClick={() => { setBookingUrl(null); setFetchedSlots(null); }}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <RefreshCw className="h-3 w-3" /> {t("myTasks.interviewScheduling.resendDifferentSlots")}
                </button>
              </div>
            ) : (
              /* Calendar connected — pick slots */
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs">
                  <Link2 className="h-3.5 w-3.5 text-green-500" />
                  <span className="text-green-600 font-medium capitalize">{t("myTasks.interviewScheduling.calendarConnected", { provider: calendarStatus.provider })}</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">{t("myTasks.interviewScheduling.from")}</label>
                    <DatePicker value={startDate} onChange={setStartDate} className="w-full text-xs" disablePastDates />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">{t("myTasks.interviewScheduling.to")}</label>
                    <DatePicker value={endDate} onChange={setEndDate} className="w-full text-xs" />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <label className="text-xs text-muted-foreground whitespace-nowrap">{t("myTasks.interviewScheduling.slotDuration")}</label>
                  <select
                    value={slotDuration}
                    onChange={(e) => setSlotDuration(Number(e.target.value))}
                    className="text-xs border rounded px-2 py-1.5 bg-background flex-1"
                  >
                    <option value={30}>{t("myTasks.interviewScheduling.minutes", { count: 30 })}</option>
                    <option value={45}>{t("myTasks.interviewScheduling.minutes", { count: 45 })}</option>
                    <option value={60}>{t("myTasks.interviewScheduling.minutes", { count: 60 })}</option>
                    <option value={90}>{t("myTasks.interviewScheduling.minutes", { count: 90 })}</option>
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleFetchSlots}
                    disabled={isFetching || !startDate || !endDate}
                    className="text-xs h-8 px-3"
                  >
                    {isFetching ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    <span className="ml-1">{t("myTasks.interviewScheduling.fetchSlots")}</span>
                  </Button>
                </div>

                {fetchedSlots !== null && (
                  <div className="space-y-2">
                    {fetchedSlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-3">{t("myTasks.interviewScheduling.noFreeSlots")}</p>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">
                          {t("myTasks.interviewScheduling.selectedSlots", { selected: selectedSlots.size, total: fetchedSlots.length })}
                        </p>
                        <div className="max-h-44 overflow-y-auto space-y-2 pr-1">
                          {Object.entries(grouped).map(([date, slots]) => (
                            <div key={date}>
                              <p className="text-xs font-medium text-muted-foreground mb-1">{fmtDate(slots[0].startsAt)}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {slots.map((s) => {
                                  const checked = selectedSlots.has(s.startsAt);
                                  return (
                                    <button
                                      key={s.startsAt}
                                      onClick={() => toggleSlot(s.startsAt)}
                                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                        checked
                                          ? "bg-violet-100 border-violet-300 text-violet-700 dark:bg-violet-900/30 dark:border-violet-700 dark:text-violet-300"
                                          : "bg-muted border-border text-muted-foreground line-through"
                                      }`}
                                    >
                                      {fmtTime(s.startsAt)}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleSendSlots}
                          disabled={isSending || selectedSlots.size === 0}
                          className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                        >
                          {isSending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                          {t("myTasks.interviewScheduling.sendSlots", { count: selectedSlots.size })}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── Schedule Directly tab ────────────────────────────────────────── */}
          <TabsContent value="direct" className="mt-4">
            <div className="space-y-5">
              {/* Calendar */}
              <div>
                <p className="text-sm font-medium mb-2">{t("myTasks.interviewScheduling.interviewDate")} <span className="text-red-500">*</span></p>
                <CalendarPicker
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < today}
                  locale={dateLocale}
                  className="rounded-lg border w-fit"
                />
              </div>

              {/* Time */}
              <div>
                <p className="text-sm font-medium mb-2">{t("myTasks.interviewScheduling.interviewTime")} <span className="text-red-500">*</span></p>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-36"
                />
              </div>

              {/* Location */}
              <div>
                <p className="text-sm font-medium mb-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground" /> {t("myTasks.interviewScheduling.location")}
                  <span className="text-muted-foreground font-normal text-xs">{t("myTasks.interviewScheduling.optional")}</span>
                </p>
                <Input
                  placeholder={t("myTasks.interviewScheduling.locationPlaceholder")}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>

              {/* Meeting link */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <Video className="w-3.5 h-3.5 text-muted-foreground" /> {t("myTasks.interviewScheduling.meetingLink")}
                    <span className="text-muted-foreground font-normal text-xs">{t("myTasks.interviewScheduling.optional")}</span>
                  </p>
                  {calendarStatus?.connected && (
                    <button
                      type="button"
                      onClick={handleGenerateLink}
                      disabled={isGeneratingLink || !date}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-950/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingLink
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("myTasks.interviewScheduling.generating")}</>
                        : <>{calendarStatus.provider === "google"
                            ? <svg className="w-3 h-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                            : <svg className="w-3 h-3" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="10" height="10" fill="#F25022"/><rect x="11" width="10" height="10" fill="#7FBA00"/><rect y="11" width="10" height="10" fill="#00A4EF"/><rect x="11" y="11" width="10" height="10" fill="#FFB900"/></svg>
                          }
                          {t("myTasks.interviewScheduling.generateProviderLink", { provider: calendarStatus.provider === "google" ? "Meet" : "Teams" })}
                        </>
                      }
                    </button>
                  )}
                </div>
                <Input
                  placeholder="https://meet.google.com/…"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                />
                {generateLinkError === "reconnect" && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                    <span>⚠</span>
                    {t("myTasks.interviewScheduling.permissionsNeeded")}{" "}
                    <a
                      href={getCalendarOAuthUrl(calendarStatus?.provider ?? "google", "/settings?tab=calendar")}
                      className="underline hover:text-amber-800 dark:hover:text-amber-200"
                    >
                      {t("myTasks.interviewScheduling.reconnectCalendar")}
                    </a>{" "}
                    {t("myTasks.interviewScheduling.enableLinkGeneration")}
                  </p>
                )}
              </div>

              {/* Preview */}
              {date && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 text-sm space-y-1">
                  <p className="font-medium text-blue-700 dark:text-blue-300">{t("myTasks.interviewScheduling.invitationSummary")}</p>
                  <p className="text-muted-foreground">{t("myTasks.interviewScheduling.dateAtTime", { date: format(date, "EEEE, d MMMM yyyy", { locale: dateLocale }), time })}</p>
                  {location && <p className="text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{location}</p>}
                  {meetingLink && <p className="text-muted-foreground flex items-center gap-1"><Video className="w-3 h-3" />{meetingLink}</p>}
                </div>
              )}

              <DialogFooter className="pt-0">
                <Button variant="outline" onClick={() => onClose()}>{t("common.cancel")}</Button>
                <Button
                  disabled={!date || submitting}
                  onClick={handleSchedule}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <CalendarCheck className="w-3.5 h-3.5 mr-1.5" />
                  {submitting ? t("myTasks.interviewScheduling.scheduling") : t("myTasks.interviewScheduling.scheduleAndSend")}
                </Button>
              </DialogFooter>
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer for slots tab — close only */}
        <div className="flex justify-end pt-1">
          <Button variant="ghost" size="sm" onClick={() => onClose()}>{t("common.close")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubmittedTaskCard({
  task,
  onArchive,
  onDelete,
}: {
  task: SubmittedTask;
  onArchive?: () => void;
  onDelete?: () => void;
}) {
  const ctx = task.task_context;
  const { t } = useTranslation();
  const isPending = task.status === "pending";
  const isApproved = task.outcome === "approved";
  const isRejected = task.outcome === "rejected";
  const isCompleted = !isPending && !isApproved && !isRejected;
  const [pendingDelete, setPendingDelete] = useState(false);

  return (
    <Card className={`border-l-2 group ${isPending ? "border-l-amber-500/50" : isApproved ? "border-l-green-500/50" : isRejected ? "border-l-red-500/50" : "border-l-muted-foreground/30"} ${!isPending ? "opacity-80 hover:opacity-100 transition-opacity" : ""}`}>
      <CardContent className="py-3 px-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-sm font-medium text-foreground">{workflowTaskTitle(task.title, t)}</p>
              {isPending && (
                <Badge variant="outline" className="text-[10px] h-5 border-amber-500/50 text-amber-600 dark:text-amber-400">
                  <Clock className="h-2.5 w-2.5 mr-1" />{t("pilot.status.pending")} </Badge>
              )}
              {isApproved && (
                <Badge variant="outline" className="text-[10px] h-5 border-green-500/50 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-2.5 w-2.5 mr-1" />{t("pilot.status.approved")} </Badge>
              )}
              {isRejected && (
                <Badge variant="outline" className="text-[10px] h-5 border-red-500/50 text-red-600 dark:text-red-400">
                  <XCircle className="h-2.5 w-2.5 mr-1" />{t("pilot.status.rejected")} </Badge>
              )}
              {isCompleted && (
                <Badge variant="outline" className="text-[10px] h-5 text-muted-foreground">
                  <CheckCircle2 className="h-2.5 w-2.5 mr-1" />Reviewed
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
              {ctx?.employeeName && <span>Employee: {ctx.employeeName}</span>}
              {ctx?.baseSalary && (
                <span>New salary: {Number(ctx.baseSalary).toLocaleString()} {ctx.currency || "EUR"}</span>
              )}
              {ctx?.reason && <span>Reason: {ctx.reason}</span>}
              <span>Submitted: {new Date(task.created_at).toLocaleDateString()}</span>
              {task.completed_at && <span>Reviewed: {new Date(task.completed_at).toLocaleDateString()}</span>}
            </div>
          </div>

          {/* Archive / Delete — only for completed submissions, revealed on hover */}
          {!isPending && (onArchive || onDelete) && (
            <div className="flex items-center shrink-0">
              {pendingDelete ? (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="destructive" size="icon" className="h-6 w-6"
                    title="Confirm delete"
                    onClick={() => { onDelete?.(); setPendingDelete(false); }}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground"
                    title="Cancel"
                    onClick={() => setPendingDelete(false)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div
                  className="overflow-hidden flex items-center gap-0.5 w-0 group-hover:w-[3.25rem] transition-[width] duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                    title={t("pilot.task.archive")}
                    onClick={() => onArchive?.()}
                  >
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                    title={t("pilot.task.delete")}
                    onClick={() => setPendingDelete(true)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── GDPR Candidate Data Deletion Dialog ──────────────────────────────────────

function GdprDeletionDialog({ task, onClose }: { task: WorkflowTask; onClose: (refresh?: boolean) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const ctx = task.context ?? {};
  const candidateName = ctx.candidateName ?? "this candidate";
  const positionTitle = ctx.positionTitle ?? "Unknown position";
  const appliedAt = ctx.appliedAt ? new Date(ctx.appliedAt).toLocaleDateString("en-GB") : "—";

  const handleDelete = async () => {
    if (!ctx.applicationId) return;
    setDeleting(true);
    try {
      await apiDeleteRecruitmentApplication(Number(ctx.applicationId), { workflowTaskId: task.id });
      toast({ title: "Candidate data permanently deleted", description: `All data for ${candidateName} has been removed from the system.` });
      qc.invalidateQueries({ queryKey: ["recruitment-applications"] });
      onClose(true);
    } catch (err: any) {
      toast({ title: "Deletion failed", description: err.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <ShieldAlert className="w-5 h-5" /> GDPR Data Deletion
          </DialogTitle>
          <DialogDescription>
            The 365-day data retention period for this candidate has elapsed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Candidate</span>
              <span className="font-medium">{candidateName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Position</span>
              <span>{positionTitle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Applied</span>
              <span>{appliedAt}</span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Deleting will permanently remove the application, resume, all answers, and interview records from the system. This action cannot be undone.
          </p>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              className="mt-0.5 accent-red-600"
            />
            <span className="text-xs">I confirm this deletion is required by GDPR/data protection policy and cannot be reversed.</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={handleDelete}
            disabled={!confirm || deleting}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {deleting ? "Deleting…" : "Delete All Data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
